<template>
  <div class="full-width mt-4">
    <v-expansion-panels
      v-model="panels"
      multiple
    >
      <v-expansion-panel
        v-for="(filter, index) in filters"
        :key="`filter_${index}`"
      >
        <v-expansion-panel-header>
          {{ filter.label }}
        </v-expansion-panel-header>
        <v-expansion-panel-content v-if="!loading">
          <FilterListItem
            v-if="filter.viewType === 'SelectGroup'"
            v-model="filterModel"
            :items="listItems(filter)"
            :filter="filter"
            :update-filter="updateFilter"
            :reference="filter.reference"
          />
          <FilterToggleItem v-if="filter.viewType === 'Switch'" v-model="filterModelSwitch" :filter="filter" :label="filter.label" />
          <CustomFields v-if="filter.custom" :filter="filter" />
        </v-expansion-panel-content>
        <v-expansion-panel-content v-else>
          <div class="d-flex justify-center align-center full-height">
            <Loader />
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api';
import { isObjectEmpty, sortArray } from '../../helpers/utils';
import { separateContentTypesWithAndWithoutSession } from '../../helpers/dataManager';

import store from '@/store';
import FilterListItem from './FilterListItem.vue';
import FilterToggleItem from './FilterToggleItem.vue';
import CustomFields from './CustomFields.vue';
import Loader from '@/components/common/Loader.vue';

const jsonFilters = require('../../configs/filters.json');

export default {
  name: 'SessionsFilters',
  components: { FilterListItem, FilterToggleItem, CustomFields, Loader },
  setup () {
    const currentTab = computed(() => store.state.currentTab);
    const references = computed(() => store.state.references);
    const sessions = computed(() => store.getters.dailySessions);
    const panels = ref([]);
    const sideBarFilters = computed(() => store.getters.sideBarFilters);
    const selectedFilters = ref(sideBarFilters.value);
    const expandListSessionType = ref(false);
    const search = ref(null);
    const loading = ref(false);

    const filterModel = ref([]);
    const filterModelSwitch = ref(false);

    watch(sideBarFilters, () => {
      selectedFilters.value = sideBarFilters.value;
    });

    const getReferences = referenceName => {
      if (references.value && !isObjectEmpty(references.value)) {
        const types = Object.entries(references.value[referenceName] || {}).map(([key, value]) => ({
          value: key,
          label: value
        }));
        return sortArray(types, 'label');
      }
      return [];
    };

    const activities = computed(() => {
      const { products } = store.state;

      const activitiesToReturn = [];

      (products || []).map(p => {
        (p.activities || []).map(a => {
          if (!activitiesToReturn.some(act => act.uuid === a.uuid)) {
            activitiesToReturn.push(a);
          }
        });
      });

      return activitiesToReturn;
    });

    const customers = computed(() => {
      const { with_session, without_session } = separateContentTypesWithAndWithoutSession('customer');

      return [...with_session, ...without_session].map(c => ({ ...c, equestrianLevel: c.equestrianLevel || c.equestrian_level }));
    });

    const formVariables = computed(() => (
      {
        sessions: sessions.value,
        activities: activities.value,
        customers: customers.value
      }
    ));

    const manageSessionTypeItemsQuantity = computed(() => (expandListSessionType.value ? 9999999 : 5));

    const filters = computed(() => {
      const filtersToreturn = [];
      jsonFilters.all.map(filter => {
        filtersToreturn.push({ ...filter, type: 'session' });
      });

      const computedFilters = jsonFilters[currentTab.value];

      (computedFilters || []).map(filter => {
        filtersToreturn.push({ ...filter, type: currentTab.value });
      });

      return filtersToreturn;
    });

    const sessionTypes = computed(() => {
      const types = (sessions.value || []).map(s => s.type);
      // uniques values
      return [...new Set(types)].map(type => ({
        value: type,
        label: store.getters.referenceValue('equestrian_session_values', type)
      }));
    });

    const slicedSessionType = computed(() => (sessionTypes.value || []).slice(0, manageSessionTypeItemsQuantity.value));

    const headers = [
      { text: '', value: 'name' },
      { text: '', value: 'count' }
    ];

    const rowClick = (item, row) => {
      row.select(!row.isSelected);
    };

    const filterDataTableSession = (value, searchValue, item) => item.label.toLowerCase().includes(searchValue.toLowerCase());

    const updateFilter = () => {
      store.commit('setSideBarFilters', selectedFilters.value);
    };

    const selectItem = () => {
      updateFilter();
    };

    const filterByStoreBase = filter => {
      const filterValues = [];

      const filterItems = store.state[filter.items] || formVariables.value[filter.customItems];

      if (filter.content) {
        (formVariables.value[filter.content] || []).map(content => {
          if (Array.isArray(content[filter.field])) {
            // product case
            content[filter.field].map(c => {
              const reference = (filterItems || []).find(i => i[filter.contentField] === c[filter.subfield]);
              if (reference) {
                const index = filterValues.findIndex(i => i.value === reference[filter.contentField]);

                if (index !== -1) {
                  filterValues[index].count += 1;
                } else {
                  filterValues.push({ label: reference[filter.contentLabel], value: reference[filter.contentField], count: 1 });
                }
              }
            });
          } else {
            // discipline
            let fieldToCompare = content[filter.field];
            if (content[filter.field] && filter.subfield) {
              fieldToCompare = content[filter.field][filter.subfield];
            }
            const reference = (filterItems || []).find(r => r[filter.contentField] === fieldToCompare);

            if (reference) {
              const index = filterValues.findIndex(i => i.value === reference[filter.contentField]);

              if (index !== -1) {
                filterValues[index].count += 1;
              } else {
                filterValues.push({ label: reference[filter.contentLabel], value: reference[filter.contentField], count: 1 });
              }
            }
          }
        });
      } else {
        // customers
        (filterItems || []).map(item => {
          const index = filterValues.findIndex(i => i.value === item[filter.field]);

          if (index !== -1) {
            filterValues[index].count += 1;
          } else if (item[filter.field]) {
            filterValues.push({ label: item[filter.field], value: item[filter.field], count: 1 });
          }
        });
      }

      return filterValues;
    };

    const filterByRerenceBased = filter => {
      const filterValues = [];
      const filterItems = getReferences(filter.reference);

      if (filter.content) {
        (formVariables.value[filter.content] || []).map(content => {
          if (Array.isArray(content[filter.field])) {
            content[filter.field].map(c => {
              const reference = (filterItems || []).find(i => i.value === c);
              if (reference) {
                const index = filterValues.findIndex(i => i.value === reference.value);

                if (index !== -1) {
                  filterValues[index].count += 1;
                } else {
                  filterValues.push({ label: reference.label, value: reference.value, count: 1 });
                }
              }
            });
          } else {
            let fieldToCompare = content[filter.field];
            if (content[filter.field] && filter.subfield) {
              fieldToCompare = content[filter.field][filter.subfield];
            }

            const reference = (filterItems || []).find(r => r.value === fieldToCompare);

            if (reference) {
              const index = filterValues.findIndex(i => i.value === reference.value);

              if (index !== -1) {
                filterValues[index].count += 1;
              } else {
                filterValues.push({ label: reference.label, value: reference.value, count: 1 });
              }
            }
          }
        });
      } else {
        // horse categories
        store.state[filter.items].map(ct => {
          if (Array.isArray(ct[filter.field])) {
            ct[filter.field].map(val => {
              const reference = (filterItems || []).find(i => i.value === val);
              if (reference) {
                const index = filterValues.findIndex(i => i.value === val);
                if (index !== -1) {
                  filterValues[index].count += 1;
                } else {
                  filterValues.push({ label: reference.label, value: reference.value, count: 1 });
                }
              }
            });
          } else {
            const reference = (filterItems || []).find(i => i.value === ct[filter.field]);
            if (reference) {
              const index = filterValues.findIndex(i => i.value === ct[filter.field]);
              if (index !== -1) {
                filterValues[index].count += 1;
              } else {
                filterValues.push({ label: reference.label, value: reference.value, count: 1 });
              }
            }
          }
        });
      }
      return filterValues;
    };

    const listItems = filter => {
      let filterValues = [];

      switch (filter.filterType) {
        case 'storeBase': {
          filterValues = filterByStoreBase(filter);
          break;
        }
        case 'referenceBased': {
          filterValues = filterByRerenceBased(filter);
          break;
        }
        default:
      }

      return sortArray(filterValues, 'label');
    };

    const getCount = item => sessions.value.filter(s => s.type === item.value).length;

    watch(panels, async () => {
      const isPorductPanelExpanded = panels.value.some(p => filters.value[p].items === 'products');

      if (isPorductPanelExpanded && !store.state.isLazyLoaded.products) {
        loading.value = true;
        await store.dispatch('getProducts');
        loading.value = false;
      }
    });

    return { loading, sideBarFilters, filterModelSwitch, filterModel, getCount, listItems, updateFilter, selectItem, filterDataTableSession, search, rowClick, headers, panels, filters, selectedFilters, sessionTypes, expandListSessionType, slicedSessionType };
  }
};
</script>

<style>
  #filterSessionTable > div > table{
    table-layout: auto;
  }

  #filterSessionTable {
    width: 300px;
  }

  #filterSessionTable tr {
    cursor: pointer;
  }
</style>
